import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import MuiSelect from '@material-ui/core/Select';
import Field from '../Form/Field';
import Dict from '../../utils/Dict';
import i18next from 'i18next';

const getChecked = (optionValue, value) =>
  Array.isArray(value) ? value.includes(optionValue) : false;

const getOptionsValue = (options, value) => {
  if (!Array.isArray(value)) return '';

  return options
    .filter(opt => value.includes(opt.code))
    .map(opt => opt[`${i18next.language}_name`])
    .join(', ');
};

function MultiSelectComponent(props) {
  const { name, value, onChange, lookupId, disabled, placeholder, multiple } = props;
  const [sOptions, setOptions] = useState([]);
  const options = props.options || sOptions || [];

  useEffect(() => {
    if (lookupId) {
      Dict.items(lookupId).then(dictOptions => setOptions(dictOptions));
    }
  }, [lookupId]);

  const getSingleOptionsValue = selectedValue => {
    const selectedOption = options.find(opt => opt.code === selectedValue);
    return selectedOption ? selectedOption[`${i18next.language}_name`] : '';
  };

  return (
    <FormControl fullWidth error={!!props.error}>

      <MuiSelect
        multiple={multiple}
        disabled={disabled}
        name={name}
        value={multiple ? (value || []) : (value || '')}
        onChange={e => onChange(e)}
        displayEmpty
        MenuProps={{ style: { maxWidth: 650 } }}
        renderValue={selected => {
          if (!selected || (multiple && selected.length === 0) || (!multiple && selected === '')) {
            return <span style={{ color: '#BFBFBF' }}>{placeholder}</span>;
          }
          return multiple
            ? getOptionsValue(options, selected)
            : getSingleOptionsValue(selected);
        }}
      >
        <MenuItem value="" disabled>
          <ListItemText primary={placeholder} />
        </MenuItem>

        {options.map(opt => (
          <MenuItem key={opt.code} value={opt.code} disabled={opt.disabled}>
            {multiple && <Checkbox checked={getChecked(opt.code, value)} />}
            <ListItemText primary={opt[`${i18next.language}_name`]} />
          </MenuItem>
        ))}
      </MuiSelect>

      {props.error && <FormHelperText>{props.helperText}</FormHelperText>}
    </FormControl>
  );
}

const MultiSelect = props => {
  const { withoutForm, name, onChange, multiple, ...restProps } = props;

  return withoutForm ? (
    <MultiSelectComponent name={name} onChange={onChange} multiple={multiple} {...restProps} />
  ) : (
    <Field name={name}>
      {({ onChange: formOnChange, ...fieldProps }) => (
        <MultiSelectComponent
          name={name}
          {...fieldProps}
          {...restProps}
          multiple={multiple}
          onChange={event => {
            formOnChange(event.target.value);
            onChange && onChange(event);
          }}
        />
      )}
    </Field>
  );
};

MultiSelect.propTypes = {
  withoutForm: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
};

MultiSelect.defaultProps = {
  withoutForm: false,
  multiple: true,
};

export default MultiSelect;